import { css } from '@emotion/react';

import { WeblogPageCategory, WeblogScreenName } from '../../../shared/constants/weblog';
import media from '../../../shared/utils/mediaQuery';
import { sendClickLog } from '../../utils/sendClickLog';

import styled from '@emotion/styled';
import { HeaderTheme, HeaderThemeType } from '../../../constants/header';
import { useLayoutHeaderContext } from '../../../modules/LayoutHeaderContext';
import Icon from '../../../shared/designSystem/components/Icon';
import { globalVariables } from '../../../shared/designSystem/constants';

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { isActiveMenu } from '../../utils/isActiveMenu';
import { useDetermineMobile } from '@myrealtrip/device-detect';

type Props = {
  label: string;
  link: string;
  showIcon?: boolean;
  showNewBadge?: boolean;
  code: string;
  iconUrl?: string;
  badge?: {
    badgeName: string;
    badgeStyle: 'solid' | 'outline';
  };
  onClick?: () => void;
};

export default function LocalNavItem({
  label,
  link,
  showIcon,
  code,
  iconUrl,
  badge,
  showNewBadge,
  onClick,
}: Props) {
  const isMobile = useDetermineMobile();
  const [isActive, setIsActive] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const { theme, isFlightMode } = useLayoutHeaderContext();
  const canShowCmsIcon = showIcon && iconUrl;
  const canShowCmsBadge = badge && badge.badgeName;
  const canShowNewBadge = !badge && showNewBadge;

  const { badgeName, badgeStyle } = badge ?? {};

  const handleClick = () => {
    sendClickLog({
      eventName: 'gnb',
      screenName: WeblogScreenName.COMMON,
      pageCategory: WeblogPageCategory.COMMON,
      itemKind: 'menu',
      itemName: label,
      targetUrl: link,
    });

    onClick?.();
  };
  const handleMouseEnter = () => setIsHover(true);
  const handleMouseLeave = () => setIsHover(false);

  const canShowActiveBar = isActive || isHover;

  useEffect(() => {
    setIsActive(isActiveMenu({ code, menuUrl: link, isFlightMode }));
  }, [code, link, isFlightMode]);

  return (
    <Container
      colorTheme={theme}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      isMobile={isMobile}
    >
      <Anchor href={link} onClick={handleClick}>
        {canShowCmsIcon && <CmsIcon src={iconUrl} alt="" />}
        <Title>{label}</Title>
      </Anchor>
      {canShowCmsBadge && <Badge outline={badgeStyle === 'outline'}>{badgeName}</Badge>}
      {canShowNewBadge && <NewBadge name="ic-badge-new" />}
      <AnimatePresence>
        {canShowActiveBar && (
          <ActiveMenuBottomBar
            colorTheme={theme}
            initial={{ opacity: isActive ? 1 : 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: isActive ? 1 : 0 }}
          />
        )}
      </AnimatePresence>
    </Container>
  );
}

const getContainerTheme = ({ colorTheme }: { colorTheme: HeaderThemeType }) => {
  return {
    [HeaderTheme.blue]: css`
      * {
        color: ${globalVariables.white};
      }
    `,
    [HeaderTheme.white]: css`
      * {
        color: ${globalVariables.gray_700};
      }
    `,
    [HeaderTheme.transparent]: css`
      * {
        color: ${globalVariables.white};
      }
    `,
  }[colorTheme];
};

const Container = styled.div<{
  colorTheme: HeaderThemeType;
  isMobile: boolean;
}>`
  height: 100%;
  position: relative;
  display: flex;
  margin: 0;

  ${({ colorTheme }) => getContainerTheme({ colorTheme })};

  &:first-of-type:not(.swiper-slide *) {
    margin-left: 0;
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      &:first-of-type {
        margin: 0 12px;
      }
    `}
`;

const ActiveMenuBottomBar = styled(motion.span, {
  shouldForwardProp: (prop) => prop !== 'colorTheme',
})<{ colorTheme: HeaderThemeType }>`
  display: block;
  position: absolute;
  width: 100%;
  height: 3px;
  bottom: 0;
  left: 0;
  background-color: ${({ colorTheme }) =>
    colorTheme === 'white' ? globalVariables.blue_300 : globalVariables.white};
`;

const Anchor = styled.a`
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: 5px;

  ${media.mobile} {
    white-space: normal;
  }
`;

const CmsIcon = styled.img`
  width: 24px;
  height: 24px;
`;
const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${globalVariables.gray_700};

  ${media.tablet} {
    font-size: 14px;
  }

  ${media.mobile} {
    font-size: 16px;
  }
`;
const Badge = styled.span<{ outline?: boolean }>`
  position: absolute;
  left: 65%;
  top: -6px;
  padding: 2px 5px;
  border-radius: 9px;
  border: 1px solid ${globalVariables.red_400};
  background-color: ${({ outline }) =>
    outline ? `${globalVariables.white};` : `${globalVariables.red_400};`};
  font-size: 9px;
  font-weight: 700;
  line-height: 12px;
  color: ${({ outline }) =>
    outline ? `${globalVariables.red_400};` : `${globalVariables.white};`};
  letter-spacing: -0.4px;
  white-space: nowrap;
`;
const NewBadge = styled(Icon)`
  position: absolute;
  right: -10px;
  top: 4px;

  ${media.mobile} {
    right: -2px;
  }
`;
